import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import './zod-setup';
import { toCssVariables, toFontFaceCss, toSpacingCss } from '@saledeed/shared';
import { ApiError } from './api/client';
import { SessionProvider } from './auth/session';
import { reloadOnceForNewVersion, router } from './router';
import './styles.css';

// The closed style set drives the on-screen document styles, so the editor,
// the preview and the PDF use the same sizes.
const tokens = document.createElement('style');
tokens.textContent = `${toFontFaceCss()}\n${toCssVariables()}\n${toSpacingCss()}`;
document.head.appendChild(tokens);

// After an update (npm run build) the page files get new names. A tab opened before it asks
// for the old ones and fails ("Failed to fetch dynamically imported module"). Reload once to
// get the new version; the guard stops a reload loop if the file is really missing.
window.addEventListener('vite:preloadError', event => {
  if (reloadOnceForNewVersion()) event.preventDefault();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retrying a 4xx never helps and can hammer a rate limit.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
